import { useNavigate, useSearchParams } from "react-router-dom";

import { Transaction as TransactionType } from "/app/src/models";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import { createColumnHelper } from "@tanstack/react-table";
import { transactionService } from "/app/src/services";
import { Helmet } from "react-helmet";
import { Search } from "/app/src/components/generic/components/search";
import Table from "/app/src/components/generic/tables/table";
import { Col, Row, Select } from "antd";
import { HistoryTypes } from "/app/src/constants/types";
import Box from "/app/src/components/generic/components/box";
import useQueryParams from "/app/src/hooks/useQueryParams";
import usePaginatedData from "/app/src/hooks/usePaginatedData";

const { Option } = Select;

/**
 *
 * @returns List of Transactions
 */
export default function Transactions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [filter, setFilter] = useState("all");

  const columnHelper = createColumnHelper<TransactionType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => info.getValue(),
        header: () => t("translation:creation_date"),
      }),
      columnHelper.accessor("type", {
        id: "type",
        cell: (info) => info.getValue(),
        header: () => t("translation:type"),
      }),
      columnHelper.accessor("exportStateType", {
        id: "exportStateType",
        cell: (info) => info.getValue(),
        header: () => t("translation:export_state"),
      }),
      columnHelper.accessor("id", {
        id: "id",
        cell: (info) => info.getValue(),
        header: () => t("translation:id"),
        meta: { clickable: false, sortable: false },
      }),
    ],
    [columnHelper, t],
  );

  /**
   *
   * @param id Transaction ID
   * @returns Navigate to the Transaction
   */
  const goToTransaction = useCallback(
    (id: number) => {
      navigate(`/explorer/transactions/${id}`);
    },
    [navigate],
  );

  const {
    data: transactions,
    count: transactionsCount,
    isFetching,
  } = usePaginatedData<TransactionType>({
    queryKey: ["transactions"],
    searchString,
    sort,
    page,
    pageSize,
    service: transactionService,
    options: { type: `[or]${filter}` },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: transactionsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  //onChange for select setFilter Callback
  const onChangeFilter = useCallback(
    (value: number[]) => {
      const deliminatedValue = value.join(";");
      setFilter(deliminatedValue);
    },
    [setFilter],
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:transactions")} - ItemPath</title>
      </Helmet>
      <Box>
        <Row>
          <Col span={12}>
            <Search box={false} allowClear setSearchString={setSearchString} />
          </Col>
          <Col span={12}>
            <Select
              onChange={onChangeFilter}
              size="large"
              style={{ width: "100%" }}
              placeholder={t("translation:filter_by_type")}
              allowClear
              mode="multiple"
            >
              {HistoryTypes.map((key) => (
                <Option key={key.value} value={key.value}>
                  {key.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Box>
      <Table
        loading={isFetching}
        rows={transactions}
        tableColumns={columns}
        length={transactionsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToTransaction}
        emptyText={`${t("translation:no")} ${t("translation:transactions")} ${t(
          "translation:found",
        )}`}
      />
    </div>
  );
}
